import type {
  AppState,
  Child,
  ExchangeRecord,
  KnowledgeItem,
  LearningProgress,
  Product,
  ReviewResult
} from '../types';
import {
  builtinCharacterKnowledge,
  builtinPoemKnowledge,
  builtinWordKnowledge
} from './builtin-library';
import { primarySchoolPoemKnowledge } from './primary-poems';
import { builtinCharacterExtraKnowledge } from './builtin-character-extra';
import { criticalCharacterKnowledge } from './critical-characters';

const now = () => new Date();

function dateAt(dayOffset: number, hour = 8, minute = 0) {
  const value = now();
  value.setDate(value.getDate() + dayOffset);
  value.setHours(hour, minute, 0, 0);
  return value.toISOString();
}

function knowledge(
  id: string,
  type: KnowledgeItem['type'],
  title: string,
  content: string,
  extra: Partial<KnowledgeItem> = {}
): KnowledgeItem {
  return {
    id,
    type,
    title,
    content,
    createdAt: dateAt(-30),
    ...extra
  };
}

const coreKnowledge: KnowledgeItem[] = [
  knowledge('char-ri', 'character', '日', '日', { pronunciation: 'rì', meaning: '太阳，也可以表示一天。' }),
  knowledge('char-yue', 'character', '月', '月', { pronunciation: 'yuè', meaning: '月亮，也可以表示月份。' }),
  knowledge('char-shan', 'character', '山', '山', { pronunciation: 'shān', meaning: '地面上高起来的部分。' }),
  knowledge('char-shui', 'character', '水', '水', { pronunciation: 'shuǐ', meaning: '可以流动、饮用的液体。' }),
  knowledge('char-huo', 'character', '火', '火', { pronunciation: 'huǒ', meaning: '燃烧时发出的光和热。' }),
  knowledge('char-mu', 'character', '木', '木', { pronunciation: 'mù', meaning: '树木，也可以表示木头。' }),
  knowledge('char-yun', 'character', '云', '云', { pronunciation: 'yún', meaning: '天空中由小水滴组成的云朵。' }),
  knowledge('char-yu', 'character', '雨', '雨', { pronunciation: 'yǔ', meaning: '从云中落下来的水滴。' }),
  knowledge('char-hua', 'character', '花', '花', { pronunciation: 'huā', meaning: '植物开放的美丽部分。' }),
  knowledge('char-niao', 'character', '鸟', '鸟', { pronunciation: 'niǎo', meaning: '有羽毛和翅膀、会飞的动物。' }),

  knowledge('poem-jingyesi', 'poem', '静夜思', '床前明月光，\n疑是地上霜。\n举头望明月，\n低头思故乡。', {
    dynasty: '唐',
    author: '李白',
    source: '知识库'
  }),
  knowledge('poem-chunxiao', 'poem', '春晓', '春眠不觉晓，\n处处闻啼鸟。\n夜来风雨声，\n花落知多少。', {
    dynasty: '唐',
    author: '孟浩然',
    source: '知识库'
  }),
  knowledge('poem-yonge', 'poem', '咏鹅', '鹅，鹅，鹅，\n曲项向天歌。\n白毛浮绿水，\n红掌拨清波。', {
    dynasty: '唐',
    author: '骆宾王',
    source: '知识库'
  }),
  knowledge('poem-dengguanquelou', 'poem', '登鹳雀楼', '白日依山尽，\n黄河入海流。\n欲穷千里目，\n更上一层楼。', {
    dynasty: '唐',
    author: '王之涣',
    source: '知识库'
  }),

  knowledge('word-apple', 'word', 'apple', '苹果\n/ˈæpəl/', {
    pronunciation: 'apple',
    meaning: '苹果',
    emoji: '🍎'
  }),
  knowledge('word-banana', 'word', 'banana', '香蕉\n/bəˈnɑːnə/', {
    pronunciation: 'banana',
    meaning: '香蕉',
    emoji: '🍌'
  }),
  knowledge('word-cat', 'word', 'cat', '猫\n/kæt/', {
    pronunciation: 'cat',
    meaning: '猫',
    emoji: '🐱'
  }),
  knowledge('word-dog', 'word', 'dog', '狗\n/dɒɡ/', {
    pronunciation: 'dog',
    meaning: '狗',
    emoji: '🐶'
  }),
  knowledge('word-sun', 'word', 'sun', '太阳\n/sʌn/', {
    pronunciation: 'sun',
    meaning: '太阳',
    emoji: '☀️'
  }),
  knowledge('word-moon', 'word', 'moon', '月亮\n/muːn/', {
    pronunciation: 'moon',
    meaning: '月亮',
    emoji: '🌙'
  }),
  knowledge('word-book', 'word', 'book', '书\n/bʊk/', {
    pronunciation: 'book',
    meaning: '书',
    emoji: '📘'
  }),
  knowledge('word-tree', 'word', 'tree', '树\n/triː/', {
    pronunciation: 'tree',
    meaning: '树',
    emoji: '🌳'
  })
];

function normalizeIdentityText(value: string) {
  return value.replace(/[\s·・《》“”"'（）()]/g, '').trim();
}

function normalizePoemBody(content: string) {
  return content.replace(/[^\u3400-\u9fff]/g, '');
}

export function knowledgeIdentityKeys(item: Pick<KnowledgeItem, 'type' | 'title' | 'author' | 'content'>) {
  if (item.type !== 'poem') return [`${item.type}:${normalizeIdentityText(item.title)}`];

  const keys = [`poem-name:${normalizeIdentityText(item.title)}:${normalizeIdentityText(item.author || '')}`];
  const body = normalizePoemBody(item.content);
  if (body.length >= 8) keys.push(`poem-body:${body}`);
  return keys;
}

export function knowledgeIdentityKey(item: Pick<KnowledgeItem, 'type' | 'title' | 'author' | 'content'>) {
  return knowledgeIdentityKeys(item)[0];
}

function mergeKnowledge(items: KnowledgeItem[]) {
  const seenIds = new Set<string>();
  const seenKeys = new Set<string>();
  return items.filter((item) => {
    const keys = knowledgeIdentityKeys(item);
    if (seenIds.has(item.id) || keys.some((key) => seenKeys.has(key))) return false;
    seenIds.add(item.id);
    keys.forEach((key) => seenKeys.add(key));
    return true;
  });
}

export const seedKnowledge: KnowledgeItem[] = mergeKnowledge([
  ...coreKnowledge,
  ...builtinCharacterKnowledge,
  ...builtinCharacterExtraKnowledge,
  ...criticalCharacterKnowledge,
  ...primarySchoolPoemKnowledge,
  ...builtinPoemKnowledge,
  ...builtinWordKnowledge
]);

export const seedChildren: Child[] = [
  {
    id: 'child-xiaoman',
    name: '小满',
    birthMonth: '2020-05',
    avatarEmoji: '🐰',
    avatarColor: '#ff8c73',
    stars: 128,
    createdAt: dateAt(-80)
  },
  {
    id: 'child-xingxing',
    name: '星星',
    birthMonth: '2022-11',
    avatarEmoji: '🦊',
    avatarColor: '#2f9d88',
    stars: 92,
    createdAt: dateAt(-56)
  }
];

function progress(
  id: string,
  childId: string,
  itemId: string,
  stage: number,
  dueOffset: number,
  correctCount = stage,
  forgotCount = 0,
  mastered = false
): LearningProgress {
  const reviewResults: ReviewResult[] = [];
  for (let index = 0; index < correctCount; index += 1) reviewResults.push('known');
  for (let index = 0; index < forgotCount; index += 1) reviewResults.push('forgot');

  const history = reviewResults.map((result, index) => {
    const stageBefore = result === 'known' ? Math.max(0, index - forgotCount) : Math.max(0, index - 1);
    const stageAfter = result === 'known' ? stageBefore + 1 : Math.max(0, stageBefore - 1);
    return {
      id: `${id}-event-${index}`,
      childId,
      itemId,
      result,
      reviewedAt: dateAt(-Math.max(0, correctCount + forgotCount - index)),
      stageBefore,
      stageAfter,
      starsDelta: result === 'known' ? 1 : -1,
      completed: mastered && index === reviewResults.length - 1
    };
  });

  return {
    id,
    childId,
    itemId,
    stage,
    learnedAt: dateAt(-14),
    masteredAt: mastered ? dateAt(-2) : undefined,
    nextReviewAt: dateAt(dueOffset),
    attempts: correctCount + forgotCount,
    correctCount,
    forgotCount,
    history
  };
}

export const seedProducts: Product[] = [
  { id: 'product-star', name: '星星贴纸', icon: '⭐', cost: 30, active: true, createdAt: dateAt(-20) },
  { id: 'product-pencil', name: '彩虹铅笔', icon: '✏️', cost: 50, active: true, createdAt: dateAt(-20) },
  { id: 'product-book', name: '绘本一本', icon: '📚', cost: 90, active: true, createdAt: dateAt(-19) },
  { id: 'product-park', name: '公园野餐', icon: '🧺', cost: 140, active: true, createdAt: dateAt(-18) },
  { id: 'product-movie', name: '亲子电影', icon: '🎬', cost: 200, active: true, createdAt: dateAt(-12) },
  { id: 'product-game', name: '周末游戏时间', icon: '🧩', cost: 220, active: true, createdAt: dateAt(-8) }
];

export const seedExchanges: ExchangeRecord[] = [
  {
    id: 'exchange-1',
    childId: 'child-xiaoman',
    productId: 'product-star',
    productName: '星星贴纸',
    icon: '⭐',
    cost: 30,
    exchangedAt: dateAt(-7)
  },
  {
    id: 'exchange-2',
    childId: 'child-xiaoman',
    productId: 'product-pencil',
    productName: '彩虹铅笔',
    icon: '✏️',
    cost: 50,
    exchangedAt: dateAt(-3)
  },
  {
    id: 'exchange-3',
    childId: 'child-xingxing',
    productId: 'product-star',
    productName: '星星贴纸',
    icon: '⭐',
    cost: 30,
    exchangedAt: dateAt(-5)
  }
];

export const seedProgress: LearningProgress[] = [
  progress('progress-a-ri', 'child-xiaoman', 'char-ri', 5, 12, 5, 0, true),
  progress('progress-a-yue', 'child-xiaoman', 'char-yue', 5, 8, 5, 1, true),
  progress('progress-a-shan', 'child-xiaoman', 'char-shan', 2, -1, 2, 1),
  progress('progress-a-shui', 'child-xiaoman', 'char-shui', 1, 0, 1, 0),
  progress('progress-a-poem', 'child-xiaoman', 'poem-jingyesi', 5, 5, 5, 1, true),
  progress('progress-a-apple', 'child-xiaoman', 'word-apple', 2, -2, 2, 2),
  progress('progress-a-cat', 'child-xiaoman', 'word-cat', 5, 10, 5, 0, true),

  progress('progress-b-ri', 'child-xingxing', 'char-ri', 5, 6, 5, 0, true),
  progress('progress-b-yue', 'child-xingxing', 'char-yue', 1, 0, 1, 1),
  progress('progress-b-shan', 'child-xingxing', 'char-shan', 3, 2, 3, 0),
  progress('progress-b-poem', 'child-xingxing', 'poem-chunxiao', 1, -1, 1, 0),
  progress('progress-b-cat', 'child-xingxing', 'word-cat', 2, -1, 2, 2),
  progress('progress-b-sun', 'child-xingxing', 'word-sun', 3, 3, 3, 0)
];

export const seedLearningPlan: string[] = [
  ...new Set(seedProgress.map((entry) => entry.itemId))
];

export const legacyDemoChildIds = ['child-xiaoman', 'child-xingxing'] as const;

export function createSeedState(): AppState {
  return {
    version: 5,
    children: [],
    knowledge: seedKnowledge,
    progress: [],
    learningPlan: [],
    products: seedProducts,
    exchanges: [],
    settings: {
      intervals: {
        character: [0, 1, 2, 4, 7],
        poem: [0, 1, 2, 4, 7],
        word: [0, 1, 2, 4, 7]
      },
      rewards: {
        character: { known: 1, forgot: -1, mastered: 5 },
        poem: { known: 2, forgot: -2, mastered: 10 },
        word: { known: 1, forgot: -1, mastered: 5 }
      }
    },
    activeChildId: null,
    updatedAt: new Date().toISOString()
  };
}
